/**
 * Four seeded catalyst headlines (F15, plan §3.3).
 *
 * Drawn from the `ticker + 'n'` stream, TWO draws per item (month, then day) in
 * tag order. The pool is built before any draw so the headline text cannot
 * consume randomness — reordering the template list would otherwise re-date
 * every headline in the app.
 *
 * Live v1 returns `catalysts: []` and the UI renders an empty-state panel rather
 * than synthetic headlines over real prices (plan §6.5a) — inventing news about
 * a real company is the one synthetic field that would be actively misleading.
 */
import {
  CATALYST_TAGS,
  type Catalyst,
  type CatalystTag,
  type Catalysts,
  type SectorId,
  type Ticker,
  type WindowKey,
} from '@meridian/contracts';

import { SECN } from '../constants/sectors';
import { fnv1a } from '../rand/fnv1a';
import { createRng } from '../rand/mulberry32';
import { seedCatalysts } from '../rand/seed';

import type { Rng } from '../rand/types';

export interface CatalystSubject {
  readonly ticker: Ticker;
  readonly name: string;
  readonly sc: SectorId;
  readonly vol: number;
  readonly div: number;
  readonly rev: number;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

/** The reference dataset is dated to a fixed forward year (demo line 817). */
const CATALYST_YEAR = 2026;
const CATALYST_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL'] as const;
const DAY_BASE = 2;
const DAY_SPAN = 26;
/** Revenue is trailing-twelve-month; the headline quotes a quarter. */
const REVENUE_QUARTERS = 4;
/** Below this annualised vol the sector headline calls the name low-volatility. */
const LOW_VOL_THRESHOLD = 0.22;
/** Above this yield the capital headline talks about the dividend, not buybacks. */
const DIVIDEND_YIELD_THRESHOLD = 2;

function headlines(s: CatalystSubject, window: WindowKey): Readonly<Record<CatalystTag, string>> {
  /* A null return reads as "not down", matching the demo's `ret >= 0`. */
  const up = (s.ret[window] ?? 0) >= 0;
  return {
    EARNINGS:
      `${s.name} posts Q2 revenue of $${(s.rev / REVENUE_QUARTERS).toFixed(1)}B, ` +
      `${up ? 'ahead of' : 'short of'} consensus; management ` +
      `${up ? 'raises' : 'trims'} full-year guidance.`,
    ANALYST:
      `${up ? 'Two desks lift price targets on ' : 'Coverage turns cautious on '}${s.ticker}` +
      ` after the ${window} move; ` +
      (up ? 'valuation now the main pushback.' : 'estimate cuts flagged for next quarter.'),
    SECTOR:
      `${SECN[s.sc]} breadth ${up ? 'improves' : 'narrows'} as rate expectations shift; ` +
      `${s.ticker} screens as a ${s.vol < LOW_VOL_THRESHOLD ? 'low-volatility' : 'high-beta'}` +
      ` expression of the theme.`,
    CAPITAL:
      s.div > DIVIDEND_YIELD_THRESHOLD
        ? `${s.name} raises the quarterly dividend, keeping the yield near ${s.div.toFixed(1)}%.`
        : `${s.name} extends the buyback authorisation; no dividend initiated.`,
  };
}

function dateFrom(R: Rng): string {
  const month = CATALYST_MONTHS[Math.floor(R() * CATALYST_MONTHS.length)] as string;
  const day = DAY_BASE + Math.floor(R() * DAY_SPAN);
  return `${month} ${String(day)} ${String(CATALYST_YEAR)}`;
}

export function computeCatalysts(subject: CatalystSubject, window: WindowKey): Catalysts {
  const R = createRng(fnv1a(seedCatalysts(subject.ticker)));
  const text = headlines(subject, window);
  return CATALYST_TAGS.map((tag): Catalyst => ({ date: dateFrom(R), tag, headline: text[tag] }));
}
